/**
 * Governed PDF upload and bounded ingestion progress for provisioning setup.
 * Does not use workspace library shell, delete flows, or activation controls.
 */

import { useRef, type ChangeEvent } from "react";
import type { GovernedLibraryDocumentSummary } from "../api/cognitoryLibraryClient";
import {
  resolveUploadProgressPresentation,
  resolveUploadProgressStage,
} from "./cognitoryLibraryUploadStage";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_LIQUID_BUTTON_STYLE,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  mainPageLiquidButtonHandlers,
} from "../theme/mainPageVisual";

export type CognitoryProvisioningSetupLibraryPanelProps = {
  visible: boolean;
  uploadBusy: boolean;
  uploadAccepted: boolean;
  uploadActiveFilename: string | null;
  uploadActiveTitle: string | null;
  uploadTrackedDocument: GovernedLibraryDocumentSummary | null;
  uploadPollTimedOut: boolean;
  uploadErrorMessage: string | null;
  postUploadLibraryStateRefreshFailed: boolean;
  onSubmitUpload: (input: { file: File; title?: string }) => void;
};

function isPdfFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf")) {
    return true;
  }
  return file.type === "application/pdf";
}

export default function CognitoryProvisioningSetupLibraryPanel(
  input: CognitoryProvisioningSetupLibraryPanelProps,
): JSX.Element | null {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  if (!input.visible) {
    return null;
  }

  const httpUploading = input.uploadBusy && !input.uploadAccepted;
  const presentation = resolveUploadProgressPresentation({
    filename: input.uploadActiveFilename ?? "your document",
    httpUploading,
    document: input.uploadTrackedDocument,
    timedOut: input.uploadPollTimedOut,
  });
  const stage = resolveUploadProgressStage({
    httpUploading,
    document: input.uploadTrackedDocument,
  });
  const showProgress =
    input.uploadBusy ||
    input.uploadAccepted ||
    input.uploadPollTimedOut ||
    input.uploadErrorMessage != null;

  const controlsDisabled = input.uploadBusy;

  const handleChooseFile = () => {
    if (controlsDisabled) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || controlsDisabled) {
      return;
    }
    if (!isPdfFile(file)) {
      return;
    }
    const title = titleInputRef.current?.value.trim();
    input.onSubmitUpload({
      file,
      ...(title ? { title } : {}),
    });
  };

  return (
    <div
      data-testid="cognitory-provisioning-setup-library-panel"
      style={{ marginBottom: "1rem" }}
    >
      <p
        style={{
          textAlign: "center",
          fontSize: "0.92rem",
          color: MAIN_PAGE_COLORS.slate,
          marginBottom: "0.65rem",
        }}
      >
        Add a governed PDF to this Cognitory library.
      </p>

      <div style={{ textAlign: "center", marginBottom: "0.65rem" }}>
        <label
          htmlFor="cognitory-provisioning-setup-upload-title"
          style={{
            display: "block",
            fontSize: "0.85rem",
            color: MAIN_PAGE_COLORS.soft,
            marginBottom: "0.35rem",
          }}
        >
          Optional title
        </label>
        <input
          id="cognitory-provisioning-setup-upload-title"
          ref={titleInputRef}
          type="text"
          data-testid="cognitory-provisioning-setup-upload-title"
          disabled={controlsDisabled}
          placeholder="Document title"
          style={{
            width: "min(100%, 20rem)",
            padding: "0.45rem 0.65rem",
            borderRadius: "8px",
            border: `1px solid ${MAIN_PAGE_COLORS.whisper}`,
          }}
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        data-testid="cognitory-provisioning-setup-upload-input"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <div style={{ textAlign: "center", marginBottom: "0.65rem" }}>
        <button
          type="button"
          data-testid="cognitory-provisioning-setup-upload-choose"
          style={{
            ...MAIN_PAGE_LIQUID_BUTTON_STYLE,
            marginTop: 0,
            opacity: controlsDisabled ? 0.65 : 1,
            cursor: controlsDisabled ? "not-allowed" : "pointer",
          }}
          disabled={controlsDisabled}
          {...(controlsDisabled ? {} : mainPageLiquidButtonHandlers)}
          onClick={handleChooseFile}
        >
          {input.uploadBusy ? "Working on upload…" : "Choose PDF to upload"}
        </button>
      </div>

      {input.uploadActiveFilename ? (
        <p
          data-testid="cognitory-provisioning-setup-upload-filename"
          style={{
            textAlign: "center",
            fontSize: "0.88rem",
            color: MAIN_PAGE_COLORS.muted,
          }}
        >
          Selected file: {input.uploadActiveFilename}
        </p>
      ) : null}

      {input.uploadActiveTitle ? (
        <p
          data-testid="cognitory-provisioning-setup-upload-display-title"
          style={{
            textAlign: "center",
            fontSize: "0.88rem",
            color: MAIN_PAGE_COLORS.muted,
          }}
        >
          Title: {input.uploadActiveTitle}
        </p>
      ) : null}

      {input.uploadErrorMessage ? (
        <p
          role="alert"
          data-testid="cognitory-provisioning-setup-upload-error"
          style={{
            textAlign: "center",
            color: MAIN_PAGE_COLORS.alertInk,
            marginTop: "0.5rem",
          }}
        >
          {input.uploadErrorMessage}
        </p>
      ) : null}

      {showProgress ? (
        <div
          data-testid="cognitory-provisioning-setup-upload-progress"
          style={{
            marginTop: "0.75rem",
            padding: "0.75rem",
            borderRadius: "10px",
            background: "rgba(241, 245, 249, 0.75)",
          }}
        >
          <p
            data-testid="cognitory-provisioning-setup-upload-stage"
            style={{
              textAlign: "center",
              color: MAIN_PAGE_COLORS.inkStrong,
              marginBottom: "0.35rem",
            }}
          >
            {presentation.stageText}
          </p>
          {presentation.secondaryText ? (
            <p
              style={{
                textAlign: "center",
                fontSize: "0.88rem",
                color: MAIN_PAGE_COLORS.soft,
                marginBottom: 0,
              }}
            >
              {presentation.secondaryText}
            </p>
          ) : null}
          {input.uploadPollTimedOut ? (
            <p
              data-testid="cognitory-provisioning-setup-upload-timeout"
              style={{
                textAlign: "center",
                fontSize: "0.88rem",
                color: MAIN_PAGE_COLORS.soft,
                marginTop: "0.5rem",
                marginBottom: 0,
              }}
            >
              Status checks stopped, but your upload was accepted. Use Refresh
              state to check library progress, or upload again if needed.
            </p>
          ) : null}
          {input.uploadAccepted && stage === "ready" ? (
            <p
              data-testid="cognitory-provisioning-setup-upload-terminal-success"
              style={{
                textAlign: "center",
                fontSize: "0.88rem",
                color: MAIN_PAGE_COLORS.soft,
                marginTop: "0.5rem",
                marginBottom: 0,
              }}
            >
              Document processing finished for this upload.
            </p>
          ) : null}
          {input.uploadAccepted && stage === "failed" ? (
            <p
              data-testid="cognitory-provisioning-setup-upload-terminal-failed"
              style={{
                textAlign: "center",
                fontSize: "0.88rem",
                color: MAIN_PAGE_COLORS.alertInk,
                marginTop: "0.5rem",
                marginBottom: 0,
              }}
            >
              This document could not be processed. You can try uploading again.
            </p>
          ) : null}
        </div>
      ) : null}

      {input.postUploadLibraryStateRefreshFailed ? (
        <p
          role="alert"
          data-testid="cognitory-provisioning-setup-upload-state-refresh-failed"
          style={{
            textAlign: "center",
            color: MAIN_PAGE_COLORS.alertInk,
            marginTop: "0.65rem",
          }}
        >
          Document processing finished, but library state could not be
          refreshed. Use Refresh state to try again.
        </p>
      ) : null}

      {input.uploadAccepted &&
      !input.uploadBusy &&
      (input.uploadPollTimedOut || stage === "failed") ? (
        <div style={{ textAlign: "center", marginTop: "0.65rem" }}>
          <button
            type="button"
            data-testid="cognitory-provisioning-setup-upload-retry"
            style={MAIN_PAGE_QUIET_BUTTON_STYLE}
            onClick={handleChooseFile}
          >
            Upload another PDF
          </button>
        </div>
      ) : null}
    </div>
  );
}
